import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { DndContext, DragOverlay, closestCorners } from '@dnd-kit/core';
import {
    Card,
    CardBody,
    Typography,
    Spinner,
    Button,
    Input,
    Select,
    Option,
} from '@material-tailwind/react';
import { PlusIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import BoardColumn from './BoardColumn';
import TaskFilterBar from './TaskFilterBar';
import {
    TASK_BOARD_PAGE_SIZE,
    TASK_BOARD_STATUSES,
    buildTaskBoardRequest
} from './taskListQuery';
import CreateTaskModal from './CreateTaskModal';

const TaskBoard = ({
    filters: externalFilters,
    filterSetters: externalFilterSetters,
    reloadTrigger = 0,
    onCreateTask,
    onTaskCreated: notifyParent,
}) => {
    const { rootUrl } = require('../../../config/config');
    const [token] = useState(localStorage.getItem('authToken') || '');

    // Internal filter state — fallback when used without TasksPage
    const [internalScope, setInternalScope] = useState('all');
    const [internalStatus, setInternalStatus] = useState('all');
    const [internalPriority, setInternalPriority] = useState('all');
    const [internalSort, setInternalSort] = useState('created_desc');
    const [internalSearchInput, setInternalSearchInput] = useState('');
    const [internalDebouncedSearch, setInternalDebouncedSearch] = useState('');

    const internalDebounceRef = useRef(null);

    const scope = externalFilters?.scope ?? internalScope;
    const status = externalFilters?.status ?? internalStatus;
    const priority = externalFilters?.priority ?? internalPriority;
    const sort = externalFilters?.sort ?? internalSort;
    const search = externalFilters?.search ?? internalDebouncedSearch;
    const searchInput = externalFilterSetters?.searchInput ?? internalSearchInput;

    const setScope = externalFilterSetters?.setScope ?? setInternalScope;
    const setStatus = externalFilterSetters?.setStatus ?? setInternalStatus;
    const setPriority = externalFilterSetters?.setPriority ?? setInternalPriority;
    const setSort = externalFilterSetters?.setSort ?? setInternalSort;
    const setSearchInput = externalFilterSetters?.setSearchInput ?? setInternalSearchInput;

    const hasActiveFilters = externalFilterSetters?.hasActiveFilters
        ?? (scope !== 'all' || status !== 'all' || priority !== 'all' || search !== '');

    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeId, setActiveId] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createModalStatus, setCreateModalStatus] = useState('open');

    const fixedStatusNames = TASK_BOARD_STATUSES.map(s => s.name);

    useEffect(() => {
        if (externalFilters) return;
        if (internalDebounceRef.current) clearTimeout(internalDebounceRef.current);
        internalDebounceRef.current = setTimeout(() => {
            setInternalDebouncedSearch(internalSearchInput);
        }, 300);
        return () => clearTimeout(internalDebounceRef.current);
    }, [internalSearchInput, externalFilters]);

    useEffect(() => {
        const fetchBoardData = async () => {
            setLoading(true);
            try {
                const { url, params } = buildTaskBoardRequest({
                    rootUrl,
                    scope,
                    status,
                    priority,
                    search,
                    sort,
                    limit: TASK_BOARD_PAGE_SIZE,
                    offset: 0,
                });

                const response = await axios.get(url, {
                    params,
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (response.data.success) {
                    setTasks(response.data.data || []);
                }
            } catch (error) {
                console.error('Error fetching board data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchBoardData();
    }, [rootUrl, token, scope, status, priority, search, sort, reloadTrigger]);

    const getTasksByStatus = statusName =>
        tasks
            .filter(t => (t.status_name || t.status) === statusName)
            .sort((a, b) => a.position - b.position);

    const resolvedStatuses = [
        ...TASK_BOARD_STATUSES,
        ...Array.from(new Set(
            tasks
                .map(t => t.status_name || t.status)
                .filter(n => n && !fixedStatusNames.includes(n))
        ))
            .sort()
            .map(name => ({ name, label: name, type: 'open', color: '#64748b' }))
    ];

    const handleDragStart = e => setActiveId(e.active.id);

    const handleDragEnd = async ({ active, over }) => {
        if (!over) { setActiveId(null); return; }

        const activeTask = tasks.find(t => t.id === active.id);
        if (activeTask && (activeTask.status_name || activeTask.status) !== over.id) {
            try {
                const response = await axios.put(
                    `${rootUrl}/api/tasks/${activeTask.id}/status`,
                    { status: over.id, user_id: 'current_user' },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                if (response.data.success) {
                    setTasks(prev =>
                        prev.map(t =>
                            t.id === activeTask.id
                                ? { ...t, status: over.id, status_name: over.id }
                                : t
                        )
                    );
                }
            } catch (error) {
                console.error('Error updating task status:', error);
            }
        }
        setActiveId(null);
    };

    const handleDragCancel = () => setActiveId(null);

    const handleTaskCreated = newTask => {
        setTasks(prev => [...prev, newTask]);
        setShowCreateModal(false);
        if (notifyParent) notifyParent(newTask);
    };

    const handleAddTask = statusName => {
        setCreateModalStatus(statusName);
        setShowCreateModal(true);
    };

    const activeTask = activeId ? tasks.find(t => t.id === activeId) : null;

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <Spinner className="h-12 w-12" color="blue" />
            </div>
        );
    }

    const isEmpty = tasks.length === 0;

    return (
        <div className="space-y-4">
            <TaskFilterBar>
                <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_180px_180px_180px_180px]">
                    <Input
                        icon={<MagnifyingGlassIcon className="h-5 w-5" />}
                        label="Search tasks..."
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                    />
                    <Select label="Scope" value={scope} onChange={setScope}>
                        <Option value="all">All Tasks</Option>
                        <Option value="assigned">Assigned to Me</Option>
                        <Option value="created">Created by Me</Option>
                    </Select>
                    <Select label="Status" value={status} onChange={setStatus}>
                        <Option value="all">All Status</Option>
                        <Option value="open">Open</Option>
                        <Option value="To Do">To Do</Option>
                        <Option value="In Progress">In Progress</Option>
                        <Option value="Review">Review</Option>
                        <Option value="Done">Done</Option>
                    </Select>
                    <Select label="Priority" value={priority} onChange={setPriority}>
                        <Option value="all">All Priority</Option>
                        <Option value="low">Low</Option>
                        <Option value="normal">Normal</Option>
                        <Option value="high">High</Option>
                        <Option value="urgent">Urgent</Option>
                    </Select>
                    <Select label="Sort by" value={sort} onChange={setSort}>
                        <Option value="created_desc">Created (newest)</Option>
                        <Option value="due_asc">Due date (soonest)</Option>
                        <Option value="priority_desc">Priority (highest)</Option>
                    </Select>
                </div>
            </TaskFilterBar>

            <Card className="shadow-sm border border-white/70">
                <CardBody className="p-4 md:p-5">
                    {isEmpty ? (
                        <div className="text-center py-16 space-y-3">
                            {hasActiveFilters ? (
                                <Typography color="gray">No tasks match your filters.</Typography>
                            ) : (
                                <>
                                    <Typography color="gray">No tasks yet.</Typography>
                                    <Button
                                        onClick={() => setShowCreateModal(true)}
                                        className="bg-[#FF5733] hover:bg-[#E64A2E]"
                                        size="sm"
                                    >
                                        <PlusIcon className="h-4 w-4 inline mr-1" />
                                        Create your first task
                                    </Button>
                                </>
                            )}
                        </div>
                    ) : (
                        <DndContext
                            collisionDetection={closestCorners}
                            onDragStart={handleDragStart}
                            onDragEnd={handleDragEnd}
                            onDragCancel={handleDragCancel}
                        >
                            <div className="flex gap-4 overflow-x-auto pb-4">
                                {resolvedStatuses.map(col => (
                                    <BoardColumn
                                        key={col.name}
                                        status={col}
                                        tasks={getTasksByStatus(col.name)}
                                        onAddTask={() => handleAddTask(col.name)}
                                    />
                                ))}
                            </div>

                            <DragOverlay>
                                {activeTask ? (
                                    <Card className="opacity-90 shadow-2xl cursor-grabbing">
                                        <CardBody className="p-4">
                                            <Typography variant="h6" color="blue-gray">
                                                {activeTask.title}
                                            </Typography>
                                        </CardBody>
                                    </Card>
                                ) : null}
                            </DragOverlay>
                        </DndContext>
                    )}
                </CardBody>
            </Card>

            {showCreateModal && (
                <CreateTaskModal
                    open={showCreateModal}
                    onClose={() => setShowCreateModal(false)}
                    onTaskCreated={handleTaskCreated}
                    defaultStatus={createModalStatus}
                />
            )}
        </div>
    );
};

export default TaskBoard;
